import { reactive } from 'vue'
import store from '@/store/index'
import moment from '@/util/MPMoment'
import { deepCopy } from '@/util/deep-copy'
import { sheetState } from '../../../sheet-state'
import { setTimeArray, sheetAxis, type TimeRow } from '../../../sheet-axis'

/*
 * 시간 행 높이 — 구 TimeHeightController 를 모듈로 옮겼다 (MDP-2295 ⑥ 2단계).
 *
 * 두 가지 일을 한다.
 *   1) 시간 칸 왼쪽 아래 손잡이를 끄는 동안의 상태. 끄는 중에는 가이드선만 움직이고,
 *      놓는 순간 높이를 확정해 sheet-axis 에 넣는다(거기서 vuex 에 요일·간격별로 기억한다).
 *   2) 그 날의 시간 칸 목록을 만드는 계산(getTimeHeight). 기억해 둔 높이가 지금 운영시간과
 *      맞으면 그대로 쓰고, 아니면 기본 높이로 새로 만든다.
 *
 * column-width 와 같은 방침으로, 화면이 전역 인스턴스에 꽂아 두던 areaHelper 는
 * 끌기를 시작할 때 인자로 받는다.
 *
 * 함께 지운 것: backgroundElement (밖에서 심기만 하고 읽는 곳 없음), startTimeKey /
 * dayIndex (대입·선언만 하고 읽는 곳 없음), handleCancel (호출처 없음).
 */

type PossibleTime = { index: number; startedAt?: unknown; endedAt?: unknown }

/** 시트가 들고 있는 선택 영역 헬퍼 중 여기서 쓰는 부분만 */
type AreaHelper = {
    isTimeSelection: boolean
    selectedRange: { start: { y?: number }; end: { y?: number } }
}


/** 더 이상 줄일 수 없는 행 높이 */
const MINIMUM_HEIGHT = 26

/** 시간 칸이 시작되는 위치. 가이드선 좌표를 시트 좌표로 옮길 때 쓴다 */
export const TIME_TOP_MARGIN = 60

export const timeHeight = reactive({
    /** 손잡이를 잡고 있는 동안 참. 시트·시간칸은 이때 다른 마우스 처리를 하지 않는다 */
    isResizing: false,
    /** 가이드선의 y 좌표 (pageY) */
    guideY: 0,
})

// 끄는 동안에만 쓰는 값 — 화면이 보지 않으므로 반응형 밖에 둔다
let targetRow: TimeRow | undefined
let targetTimeStrings: string[] = []
let startPositionY = 0

const possibleTimes = (): PossibleTime[] =>
    (sheetState.reservationSetting as { possibleTimes?: PossibleTime[] } | undefined)?.possibleTimes ?? []

/**
 * 이번 끌기로 높이가 바뀔 행들.
 *
 * 시간 칸을 세로로 끌어 범위를 잡아 두었다면 그 범위 전체가 대상이고, 아니면 잡은 행 하나뿐이다.
 * 범위가 비어 있으면 start.y 가 undefined 라 아래 반복문은 돌지 않는다.
 */
function resizeTargets(timeString: string, areaHelper: AreaHelper): string[] {
    const keys: string[] = []

    if (areaHelper.isTimeSelection) {
        let startY = areaHelper.selectedRange.start.y
        let endY = areaHelper.selectedRange.end.y
        if (typeof startY === 'number' && typeof endY === 'number') {
            if (endY < startY) {
                const temp = endY
                endY = startY
                startY = temp
            }
            for (let i = startY; i <= endY; i++) {
                keys.push(sheetAxis.timeArray[i]?.timeString as string)
            }
        }
    }
    if (keys.includes(timeString) == false) { keys.push(timeString) }

    return keys
}

export function startTimeResize(
    e: MouseEvent,
    timeString: string,
    context: { areaHelper: AreaHelper },
): void {
    timeHeight.isResizing = true
    targetTimeStrings = resizeTargets(timeString, context.areaHelper)
    targetRow = sheetAxis.timeArray.find((t) => t.timeString == timeString)

    startPositionY = e.pageY
    timeHeight.guideY = e.pageY
}

/** 가이드선을 따라 움직인다. 최소 높이보다 낮아지는 자리에서는 더 올라오지 않는다 */
export function moveTimeResize(e: MouseEvent): void {
    let yPosition = e.pageY
    const height = targetRow?.height ?? 0
    if (height - (startPositionY - yPosition) < MINIMUM_HEIGHT) {
        yPosition = -height + startPositionY + MINIMUM_HEIGHT
    }

    timeHeight.guideY = yPosition
}

/** 놓는 순간 대상 행 전부에 같은 높이를 확정한다 */
export function endTimeResize(): void {
    timeHeight.isResizing = false

    const rows = (deepCopy(sheetAxis.timeArray) ?? []) as TimeRow[]
    const targetHeight = (targetRow?.height ?? 0) - (startPositionY - timeHeight.guideY)

    for (const timeString of targetTimeStrings) {
        const row = rows.find((t) => t.timeString == timeString)
        if (row != undefined) { row.height = targetHeight }
    }

    setTimeArray(rows)
}

// ─────────────────────────── 시간 칸 만들기 ───────────────────────────

/** 그 날 운영시간을 간격만큼 잘라 기본 높이의 칸 목록을 만든다. 정각이 바뀌는 칸은 굵게 표시한다 */
function defaultRows(selectedDate: unknown, interval: number): TimeRow[] {
    const dayIndex = (selectedDate as { days(): number }).days()
    const currentTimeObj = possibleTimes().find((t) => t.index == dayIndex)
    const s = moment(currentTimeObj?.startedAt)
    const e = moment(currentTimeObj?.endedAt)
    const startedAt = moment(selectedDate).startOf('days').add(s.hour(), 'hour').add(s.minute(), 'minute')
    const endedAt = moment(selectedDate).startOf('days').add(e.hour(), 'hour').add(e.minute(), 'minute')
    const currentTime = moment(startedAt)
    const defaultHeight = store.state.V2ReservationCellConfig.defaultHeight

    let index = 0
    const rows: TimeRow[] = []
    while (currentTime.isSameOrBefore(endedAt)) {
        const t = moment(currentTime)
        currentTime.add(interval, 'minutes')

        const previous = rows[rows.length - 1]?.time as { hours(): number } | undefined
        rows.push({
            index,
            timeString: t.format('HH:mm'),
            time: moment(t),
            isBold: !startedAt.isSame(currentTime) && previous != undefined && previous.hours() != t.hours(),
            height: defaultHeight,
        })
        index += 1
    }

    return rows
}

/**
 * 기억해 둔 높이가 지금 운영시간과 맞는지 본다.
 *
 * 운영시간이 바뀌면 기억해 둔 칸 목록의 양 끝이 어긋나므로 그대로 쓸 수 없다.
 * 그 날 운영시간 자체를 못 찾은 경우도 맞지 않는 것으로 본다 — 구 컨트롤러는 여기서
 * 그대로 터졌다(시트 전체가 안 그려진다). 기본 높이로 새로 만드는 쪽이 맞다.
 */
function matchesOperatingHours(rows: TimeRow[], selectedDate: unknown): boolean {
    const dayIndex = (selectedDate as { days(): number }).days()
    const timeObject = possibleTimes().find((t) => t.index == dayIndex)
    if (timeObject == undefined) { return false }

    const s = moment(timeObject.startedAt).format('HH:mm')
    const e = moment(timeObject.endedAt).format('HH:mm')
    const first = moment(rows[0].time).format('HH:mm')
    const last = moment(rows[rows.length - 1]?.time).format('HH:mm')
    return s == first && e == last
}

/**
 * 그 날 그릴 시간 칸 목록.
 *
 * 요일·간격별로 기억해 둔 높이가 있으면 그것을, 없거나 운영시간과 어긋나면 기본 높이를 쓴다.
 * 기억해 둔 값의 time 은 저장 당시 날짜라 보고 있는 날짜로 옮겨 준다.
 */
export function getTimeHeight(hospitalID: string, selectedDate: unknown): TimeRow[] {
    const dayIndex = moment(selectedDate).days()
    const interval = sheetAxis.selectedTimeInterval
    const stored = store.state.V2ReservationCellConfig.height[hospitalID] ?? []
    let rows = stored.find((h) => h.dayIndex == dayIndex && h.interval == interval)?.values

    if (rows == undefined || rows.length == 0 || matchesOperatingHours(rows, selectedDate) == false) {
        rows = defaultRows(selectedDate, interval)
    }

    return (deepCopy(rows) ?? []).map((row) => {
        const originTime = moment(row.time)
        return {
            ...row,
            time: moment(selectedDate).startOf('days').set('hour', originTime.hour()).set('minute', originTime.minute()),
        }
    })
}
